/**
 * S/R Levels Toggle Component
 *
 * Action bar button with expandable dropdown for toggling S/R display
 * and selecting which timeframes to show. Follows ActionBarPositions pattern.
 * Settings auto-save on change with debounce.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TrendingUp, Check, Eye, EyeOff, X, Loader2, LineChart, Table2, RefreshCw } from 'lucide-react';
import { useSRStore, SR_TIMEFRAME_CONFIG, ALL_SR_TIMEFRAMES, srSettingsPayload } from '../store/srStore';
import { useMarketStore } from '../store/marketStore';
import { useToastStore } from '../store/toastStore';
import { wsService } from '../services/WebSocketService';
import { API_BASE_URL } from '../config/ports';
import type { SRTimeframe } from '@shared/types';
import './SRToggle.css';

const SRToggle: React.FC<{ onOpenSRAlerts?: () => void }> = ({ onOpenSRAlerts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingToastIdRef = useRef<string | null>(null);
  const pendingHydrationVersionRef = useRef<number | null>(null);
  const pendingToastMessageRef = useRef<string | null>(null);
  const completionToastIdRef = useRef<string | null>(null);
  const lastCompletionToastRef = useRef<{ message: string; timestamp: number } | null>(null);
  const latestPendingSaveVersionRef = useRef(0);
  const [pendingSaveVersion, setPendingSaveVersion] = useState(0);

  const enabled = useSRStore((state) => state.enabled);
  const visibleTimeframes = useSRStore((state) => state.visibleTimeframes);
  const priceRange = useSRStore((state) => state.priceRange);
  const recalcInterval = useSRStore((state) => state.recalcInterval);
  const deltaPercent = useSRStore((state) => state.deltaPercent);
  const minTouchCount = useSRStore((state) => state.minTouchCount);
  const levelsPerTf = useSRStore((state) => state.levelsPerTf);
  const nearestLevels = useSRStore((state) => state.nearestLevels);
  const displayMode = useSRStore((state) => state.displayMode);
  const lineOpacity = useSRStore((state) => state.lineOpacity);
  const hydrationVersion = useSRStore((state) => state.hydrationVersion);
  const toggleTimeframe = useSRStore((state) => state.toggleTimeframe);
  const setEnabled = useSRStore((state) => state.setEnabled);
  const setVisibleTimeframes = useSRStore((state) => state.setVisibleTimeframes);
  const setPriceRange = useSRStore((state) => state.setPriceRange);
  const setRecalcInterval = useSRStore((state) => state.setRecalcInterval);
  const setDeltaPercent = useSRStore((state) => state.setDeltaPercent);
  const setMinTouchCount = useSRStore((state) => state.setMinTouchCount);
  const setLevelsPerTf = useSRStore((state) => state.setLevelsPerTf);
  const setNearestLevels = useSRStore((state) => state.setNearestLevels);
  const setDisplayMode = useSRStore((state) => state.setDisplayMode);
  const setLineOpacity = useSRStore((state) => state.setLineOpacity);
  const getEffectiveSettings = useSRStore((state) => state.getEffectiveSettings);
  const setSymbolSetting = useSRStore((state) => state.setSymbolSetting);
  const fetchSymbolSettings = useSRStore((state) => state.fetchSymbolSettings);
  const saveSymbolSettings = useSRStore((state) => state.saveSymbolSettings);
  const activeSymbol = useMarketStore((state) => state.symbol);
  const addToast = useToastStore((state) => state.addToast);
  const removeToast = useToastStore((state) => state.removeToast);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const queueSettingsSave = useCallback(() => {
    setPendingSaveVersion((version) => {
      const nextVersion = version + 1;
      latestPendingSaveVersionRef.current = nextVersion;
      return nextVersion;
    });
  }, []);

  const clearPendingToast = useCallback(() => {
    if (!pendingToastIdRef.current) {
      return;
    }
    removeToast(pendingToastIdRef.current);
    pendingToastIdRef.current = null;
    pendingHydrationVersionRef.current = null;
    pendingToastMessageRef.current = null;
  }, [removeToast]);

  const showCompletionToast = useCallback((message: string) => {
    const now = Date.now();
    const lastCompletion = lastCompletionToastRef.current;
    if (lastCompletion && lastCompletion.message === message && now - lastCompletion.timestamp < 2000) {
      return;
    }

    if (completionToastIdRef.current) {
      removeToast(completionToastIdRef.current);
    }

    completionToastIdRef.current = addToast({
      type: 'success',
      message,
    });
    lastCompletionToastRef.current = { message, timestamp: now };
  }, [addToast, removeToast]);

  const startHydrationToast = useCallback((message: string) => {
    clearPendingToast();
    pendingHydrationVersionRef.current = hydrationVersion;
    pendingToastMessageRef.current = message;
    pendingToastIdRef.current = addToast({
      type: 'info',
      message,
      duration: 0,
    });
  }, [addToast, clearPendingToast, hydrationVersion]);

  // Auto-save settings to backend with debounce
  const saveSettings = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sr-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(srSettingsPayload({
          enabled,
          visibleTimeframes,
          priceRange,
          recalcInterval,
          deltaPercent,
          minTouchCount,
          levelsPerTf,
          nearestLevels,
          displayMode,
          lineOpacity,
        })),
      });

      if (response.ok) {
        startHydrationToast('Updating S/R levels...');
        setLastSaved(Date.now());
        // Request fresh S/R levels after settings saved
        wsService.emit('get-sr-levels', { symbol: '' });
        console.log('📊 [S/R] Settings auto-saved');
      } else {
        console.error('📊 [S/R] Failed to save settings:', response.statusText);
        clearPendingToast();
        addToast({
          type: 'error',
          message: 'Failed to save S/R settings',
        });
      }
    } catch (err) {
      console.error('📊 [S/R] Failed to save settings:', err);
      clearPendingToast();
      addToast({
        type: 'error',
        message: 'Failed to save S/R settings',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    enabled,
    visibleTimeframes,
    priceRange,
    recalcInterval,
    deltaPercent,
    minTouchCount,
    levelsPerTf,
    nearestLevels,
    displayMode,
    lineOpacity,
    startHydrationToast,
    clearPendingToast,
    addToast,
  ]);

  // Pivot & Recalculate - triggers immediate recalculation around current price
  const handleRecalculate = useCallback(async () => {
    setIsRecalculating(true);
    startHydrationToast('Recalculating S/R levels...');
    try {
      const response = await fetch(`${API_BASE_URL}/sr-levels/recalculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`📊 [S/R] Recalculated ${result.totalLevels} levels around current price`);
      } else {
        console.error('📊 [S/R] Recalculate failed:', response.statusText);
        clearPendingToast();
        addToast({
          type: 'error',
          message: 'Failed to recalculate S/R levels',
        });
      }
    } catch (err) {
      console.error('📊 [S/R] Recalculate failed:', err);
      clearPendingToast();
      addToast({
        type: 'error',
        message: 'Failed to recalculate S/R levels',
      });
    } finally {
      setIsRecalculating(false);
    }
  }, [startHydrationToast, clearPendingToast, addToast]);

  // Auto-save after explicit user edits (debounced)
  useEffect(() => {
    if (pendingSaveVersion === 0) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const scheduledVersion = pendingSaveVersion;
    saveTimeoutRef.current = setTimeout(() => {
      if (latestPendingSaveVersionRef.current !== scheduledVersion) {
        return;
      }
      saveSettings();
      latestPendingSaveVersionRef.current = 0;
      setPendingSaveVersion(0);
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [pendingSaveVersion, saveSettings]);

  useEffect(() => {
    if (!pendingToastIdRef.current || pendingHydrationVersionRef.current === null) {
      return;
    }

    if (hydrationVersion <= pendingHydrationVersionRef.current) {
      return;
    }

    const wasRecalculate = pendingToastMessageRef.current?.includes('Recalculating');
    clearPendingToast();
    showCompletionToast(wasRecalculate ? 'S/R levels refreshed' : 'S/R settings applied');
  }, [hydrationVersion, clearPendingToast, showCompletionToast]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      clearPendingToast();
      if (completionToastIdRef.current) {
        removeToast(completionToastIdRef.current);
      }
    };
  }, [clearPendingToast, removeToast]);

  // Local handlers that just update store (auto-save handles the rest)
  const handleEnabledChange = useCallback((newEnabled: boolean) => {
    setEnabled(newEnabled);
    queueSettingsSave();
  }, [setEnabled, queueSettingsSave]);

  const handleToggleAll = useCallback(() => {
    const allVisible = ALL_SR_TIMEFRAMES.every(tf => visibleTimeframes.has(tf));
    const newTimeframes = allVisible ? [] : ALL_SR_TIMEFRAMES;
    setVisibleTimeframes(newTimeframes);
    queueSettingsSave();
  }, [visibleTimeframes, setVisibleTimeframes, queueSettingsSave]);

  const handleTimeframeToggle = useCallback((tf: SRTimeframe) => {
    // Guardrail: prevent toggling off the last visible timeframe
    if (visibleTimeframes.has(tf) && visibleTimeframes.size <= 1) return;
    toggleTimeframe(tf);
    queueSettingsSave();
  }, [toggleTimeframe, queueSettingsSave, visibleTimeframes]);

  const handlePriceRangeChange = useCallback((range: number) => {
    setPriceRange(range);
    queueSettingsSave();
  }, [setPriceRange, queueSettingsSave]);

  const handleIntervalChange = useCallback((minutes: number) => {
    setRecalcInterval(minutes);
    queueSettingsSave();
  }, [setRecalcInterval, queueSettingsSave]);

  const handleDeltaChange = useCallback((delta: number) => {
    setDeltaPercent(delta);
    queueSettingsSave();
  }, [setDeltaPercent, queueSettingsSave]);

  const handleMinTouchChange = useCallback((count: number) => {
    setMinTouchCount(count);
    queueSettingsSave();
  }, [setMinTouchCount, queueSettingsSave]);

  const handleLevelsPerTfChange = useCallback((count: number) => {
    setLevelsPerTf(count);
    queueSettingsSave();
  }, [setLevelsPerTf, queueSettingsSave]);

  const handleNearestLevelsChange = useCallback((count: number) => {
    setNearestLevels(count);
    queueSettingsSave();
  }, [setNearestLevels, queueSettingsSave]);

  const handleDisplayModeChange = useCallback((mode: 'chart' | 'table') => {
    setDisplayMode(mode);
    queueSettingsSave();
  }, [setDisplayMode, queueSettingsSave]);

  // Effective per-symbol max levels
  const effectiveSettings = getEffectiveSettings(activeSymbol);
  const effectiveMaxLevels = effectiveSettings.maxLevels;

  const handleMaxLevelsChange = useCallback((value: number) => {
    setSymbolSetting(activeSymbol, 'maxLevels', value);
    saveSymbolSettings(activeSymbol);
  }, [activeSymbol, setSymbolSetting, saveSymbolSettings]);

  // Fetch per-symbol settings when symbol changes
  useEffect(() => {
    if (activeSymbol) {
      fetchSymbolSettings(activeSymbol);
    }
  }, [activeSymbol, fetchSymbolSettings]);

  // Calculate dropdown position
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.left - 80), // Offset left since dropdown is wider
      });
    }
  }, [isExpanded]);

  // Click outside to close
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideExpanded = expandedRef.current && !expandedRef.current.contains(target);

      if (isOutsideContainer && isOutsideExpanded) {
        setIsExpanded(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const allSelected = ALL_SR_TIMEFRAMES.every(tf => visibleTimeframes.has(tf));
  const visibleCount = ALL_SR_TIMEFRAMES.filter(tf => visibleTimeframes.has(tf)).length;

  // Show "Saved" briefly after save completes
  const showSaved = lastSaved && Date.now() - lastSaved < 2000;

  return (
    <div ref={containerRef} className="sr-toggle">
      <button
        className={`sr-toggle-summary ${enabled ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
        onClick={handleToggleExpand}
        title="S/R Levels Settings"
      >
        <TrendingUp size={12} className="sr-toggle-icon" />
        <span className="sr-toggle-label">S/R</span>
        {enabled && visibleCount > 0 && (
          <span className="sr-toggle-count">{visibleCount}</span>
        )}
      </button>

      {isExpanded && createPortal(
        <div
          ref={expandedRef}
          className="sr-toggle-expanded-portal"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 1000,
          }}
        >
          <div className="sr-toggle-expanded">
            {/* Header */}
            <div className="sr-toggle-header">
              <span className="sr-toggle-title">S/R Levels</span>
              <div className="sr-toggle-header-actions">
                <button
                  className="sr-toggle-recalc-btn"
                  onClick={handleRecalculate}
                  disabled={!enabled || isRecalculating}
                  title="Pivot & Recalculate"
                >
                  {isRecalculating ? (
                    <Loader2 size={12} className="sr-toggle-spinner" />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                </button>
                <button
                  className={`sr-toggle-master-btn ${enabled ? 'active' : ''}`}
                  onClick={() => handleEnabledChange(!enabled)}
                  title={enabled ? 'Hide all S/R lines' : 'Show S/R lines'}
                >
                  {enabled ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
                <button className="sr-toggle-close-btn" onClick={handleClose}>
                  <X size={12} />
                </button>
              </div>
            </div>

            {/* Display Mode Toggle */}
            <div className="sr-toggle-display-mode">
              <button
                className={`sr-toggle-mode-btn ${displayMode === 'chart' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('chart')}
                disabled={!enabled}
                title="Show as chart lines"
              >
                <LineChart size={12} />
                <span>Chart</span>
              </button>
              <button
                className={`sr-toggle-mode-btn ${displayMode === 'table' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('table')}
                disabled={!enabled}
                title="Show as table"
              >
                <Table2 size={12} />
                <span>Table</span>
              </button>
            </div>

            {/* Timeframe toggles */}
            <div className="sr-toggle-content">
              <div className="sr-toggle-section-header">
                <span>Timeframes</span>
                <button
                  className="sr-toggle-all-btn"
                  onClick={handleToggleAll}
                  disabled={!enabled}
                >
                  {allSelected ? 'None' : 'All'}
                </button>
              </div>

              <div className="sr-toggle-list">
                {ALL_SR_TIMEFRAMES.map((tf) => {
                  const config = SR_TIMEFRAME_CONFIG[tf];
                  const isVisible = visibleTimeframes.has(tf);

                  return (
                    <button
                      key={tf}
                      className={`sr-toggle-item ${isVisible ? 'active' : ''}`}
                      onClick={() => handleTimeframeToggle(tf)}
                      disabled={!enabled}
                    >
                      <span
                        className="sr-toggle-color-bar"
                        style={{
                          backgroundColor: config.color,
                          width: config.width * 3 + 4,
                          borderStyle: config.style === 'dashed' ? 'dashed' : 'solid',
                        }}
                      />
                      <span className="sr-toggle-tf-label">{config.label}</span>
                      {isVisible && <Check size={10} className="sr-toggle-check" />}
                    </button>
                  );
                })}
              </div>

              {/* Price Range Slider */}
              <div className="sr-toggle-range-section">
                <div className="sr-toggle-range-header">
                  <span>Range</span>
                  <span className="sr-toggle-range-value">±{priceRange} pts</span>
                </div>
                <input
                  type="range"
                  className="sr-toggle-range-slider"
                  min={50}
                  max={500}
                  step={25}
                  value={priceRange}
                  onChange={(e) => handlePriceRangeChange(Number(e.target.value))}
                  disabled={!enabled}
                />
              </div>

              {/* Max Levels (per-symbol) */}
              <div className="sr-toggle-range-section">
                <div className="sr-toggle-range-header">
                  <span>Max Levels {activeSymbol && useSRStore.getState().perSymbolSettings[activeSymbol]?.maxLevels !== undefined ? `(${activeSymbol.split('.')[0]})` : ''}</span>
                  <span className="sr-toggle-range-value">{effectiveMaxLevels === 0 ? 'All' : effectiveMaxLevels}</span>
                </div>
                <input
                  type="range"
                  className="sr-toggle-range-slider"
                  min={50}
                  max={500}
                  step={25}
                  value={effectiveMaxLevels}
                  onChange={(e) => handleMaxLevelsChange(Number(e.target.value))}
                  disabled={!enabled}
                />
              </div>

              {/* Recalculation Interval */}
              <div className="sr-toggle-interval-section">
                <div className="sr-toggle-section-header">
                  <span>Recalc Interval</span>
                </div>
                <div className="sr-toggle-interval-options">
                  {[15, 60, 240].map((mins) => (
                    <button
                      key={mins}
                      className={`sr-toggle-interval-btn ${recalcInterval === mins ? 'active' : ''}`}
                      onClick={() => handleIntervalChange(mins)}
                      disabled={!enabled}
                    >
                      {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Delta Tolerance */}
              <div className="sr-toggle-touches-section">
                <div className="sr-toggle-section-header">
                  <span>Delta: {(deltaPercent * 100).toFixed(2)}%</span>
                </div>
                <div className="sr-toggle-touches-slider">
                  <input
                    type="range"
                    min="0.0005"
                    max="0.003"
                    step="0.0005"
                    value={deltaPercent}
                    onChange={(e) => {
                      const raw = Number(e.target.value);
                      const snapped = Math.round(raw / 0.0005) * 0.0005;
                      handleDeltaChange(snapped);
                    }}
                    disabled={!enabled}
                    className="sr-touches-slider"
                  />
                  <div className="sr-touches-slider-labels">
                    <span>0.05%</span>
                    <span>0.30%</span>
                  </div>
                </div>
              </div>

              {/* Min Touches Filter */}
              <div className="sr-toggle-touches-section">
                <div className="sr-toggle-section-header">
                  <span>Min Touches: {minTouchCount === 0 ? 'All' : `${minTouchCount}+`}</span>
                </div>
                <div className="sr-toggle-touches-slider">
                  <input
                    type="range"
                    min="0"
                    max="300"
                    step="5"
                    value={minTouchCount}
                    onChange={(e) => handleMinTouchChange(Number(e.target.value))}
                    disabled={!enabled}
                    className="sr-touches-slider"
                  />
                  <div className="sr-touches-slider-labels">
                    <span>All</span>
                    <span>300+</span>
                  </div>
                </div>
              </div>

              {/* Levels Per Timeframe */}
              <div className="sr-toggle-levels-section">
                <div className="sr-toggle-section-header">
                  <span>Levels/TF: {levelsPerTf}</span>
                </div>
                <div className="sr-toggle-touches-slider">
                  <input
                    type="range"
                    min="25"
                    max="1000"
                    step="25"
                    value={levelsPerTf}
                    onChange={(e) => handleLevelsPerTfChange(Number(e.target.value))}
                    disabled={!enabled}
                    className="sr-touches-slider"
                  />
                  <div className="sr-touches-slider-labels">
                    <span>25</span>
                    <span>1000</span>
                  </div>
                </div>
              </div>

              {/* Nearest Levels (proximity filter) */}
              <div className="sr-toggle-levels-section">
                <div className="sr-toggle-section-header">
                  <span>Nearest</span>
                </div>
                <div className="sr-toggle-levels-options">
                  {[
                    { value: 3, label: '3' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 0, label: 'All' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      className={`sr-toggle-levels-btn ${nearestLevels === opt.value ? 'active' : ''}`}
                      onClick={() => handleNearestLevelsChange(opt.value)}
                      disabled={!enabled}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Line Opacity */}
              <div className="sr-toggle-touches-section">
                <div className="sr-toggle-section-header">
                  <span>Line Opacity: {Math.round(lineOpacity * 100)}%</span>
                </div>
                <div className="sr-toggle-touches-slider">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={Math.round(lineOpacity * 100)}
                    onChange={(e) => {
                      setLineOpacity(Number(e.target.value) / 100);
                      queueSettingsSave();
                    }}
                    disabled={!enabled}
                    className="sr-touches-slider"
                  />
                  <div className="sr-touches-slider-labels">
                    <span>10%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with auto-save status */}
            <div className="sr-toggle-footer">
              {onOpenSRAlerts && (
                <button className="sr-toggle-alerts-btn" onClick={() => { setIsExpanded(false); onOpenSRAlerts(); }}>
                  SR Alerts Settings
                </button>
              )}
              <span className="sr-toggle-footer-text">Higher TF = more significant</span>
              <span className="sr-toggle-autosave-status">
                {isSaving ? (
                  <>
                    <Loader2 size={10} className="sr-toggle-spinner" />
                    Saving...
                  </>
                ) : showSaved ? (
                  <>
                    <Check size={10} />
                    Saved
                  </>
                ) : (
                  'Auto-save'
                )}
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SRToggle;
